import type { Project, Standard } from '@elec/domain';
import type { Analysis } from '@elec/analysis';
import { runRules, type Finding, type Rule, type RuleInfo } from './core.ts';
import { RGIE_RULES } from './rgie.ts';
import { NFC15100_RULES } from './nfc15100.ts';
import { NEN1010_RULES } from './nen1010.ts';
import { MEASUREMENT_RULES } from './measure.ts';
import { PV_RULES } from './pv.ts';

export type { Finding, RuleInfo, Severity } from './core.ts';
export { boardSheets, hasCableRoute } from './board.ts';
export { homeGuide, type HomeBoard, type HomeCircuit, type HomeRcd } from './home.ts';
export { DEDICATED, earthElectrode, earthNeeds, MAX_RATING_A, pointRooms } from './core.ts';
export {
  continuityMaxOhm,
  DEFAULT_TEST_VOLTAGE_V,
  insulationMinMOhm,
  latestMeasurements,
  limitText,
  loopMaxOhm,
  measurementTargets,
  measurementVerdicts,
  rcdMaxTripMs,
  type FieldVerdict,
  type MeasureField,
} from './measure.ts';

/**
 * Rule pack per standard. The test-measurement limits come from the IEC 60364-6 / IEC 61008
 * family the three standards share, so every pack checks them.
 */
export const PACKS: Record<Standard, Rule[]> = {
  rgie: [...RGIE_RULES, ...PV_RULES, ...MEASUREMENT_RULES],
  nfc15100: [...NFC15100_RULES, ...MEASUREMENT_RULES],
  nen1010: [...NEN1010_RULES, ...MEASUREMENT_RULES],
};

/** The standard a project is checked against (Belgian RGIE when not set). */
export const standardOf = (project: Project): Standard => project.meta.standard ?? 'rgie';

/** Rules of a standard's pack, as shown to the user. */
export const rulesOf = (standard: Standard): RuleInfo[] => PACKS[standard].map((r) => r.info);

/** RGIE rules (kept for callers written before the other packs). */
export const RULE_INFO: RuleInfo[] = rulesOf('rgie');

/** A rule's description, worded for the standard (shared rules may differ per pack). */
export const ruleInfo = (id: string, standard: Standard = 'rgie') =>
  PACKS[standard].find((r) => r.info.id === id)?.info;

/** Check a project against its standard. */
export function checkProject(project: Project, analysis?: Analysis): Finding[] {
  const standard = standardOf(project);
  return runRules(PACKS[standard], project, standard, analysis);
}
